import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import styled from 'styled-components'

import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import CreditCard from 'components/UI/Modal/CreditCard/CreditCard'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { initialPrefillData } from 'components-ref/organisms/wallet'
import { useModalContext } from 'context/modalContext'
import {
  deleteCard,
  editCard,
  getListCards,
  getSingleCard,
  putDefaultCard
} from 'services/endpoints/payment'
import { useAuthStore, useUserProfile } from 'store'
import colors from 'styles/colors'
import {
  type AddCard,
  type AddCardInfoRequestDto,
  type Card,
  type PrefillData
} from 'types/api/payment'

import ConfirmModal from '../../../../components/UI/Modal/Confirm/ConfirmModal'

interface CardsProps {
  setPrefillData: (_prefillData: PrefillData) => void
}
type ComparableCard = {
  id: string
  card_number: string
  expiration_month: string
  expiration_year: string
  is_default: boolean
  is_expired: boolean
  is_verified: boolean
}

const cardsMatch = (
  a: ComparableCard[],
  b: ComparableCard[]
) => {
  if (a.length !== b.length) return false

  return a.every((card, index) => {
    const other = b[index]

    return (
      card.id === other?.id &&
      card.card_number === other?.card_number &&
      card.expiration_month === other?.expiration_month &&
      card.expiration_year === other?.expiration_year &&
      card.is_default === other?.is_default &&
      card.is_expired === other?.is_expired &&
      card.is_verified === other?.is_verified
    )
  })
}
export const Cards = ({ setPrefillData }: CardsProps) => {
  const userData = useUserProfile()!
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const modalData = useModalContext()
  const setUserProfile = useAuthStore(state => state.setUserProfile)
  /**
   * Prefer cards from user profile if present.
   * Keep getListCards as a backup / refresh path.
   */
const { data: cardsList } = useQuery(['allCards'], getListCards, {
  refetchOnWindowFocus: false,
  staleTime: 10000
})

const cards: Card[] = Array.isArray(cardsList?.cards)
  ? cardsList.cards
  : []

useEffect(() => {
  if (!cardsList || !Array.isArray(cardsList.cards)) return

  const currentCards = Array.isArray(userData?.cards)
    ? userData.cards
    : []

  if (!cardsMatch(currentCards, cardsList.cards)) {
    setUserProfile({
      cards: cardsList.cards
    })
  }
}, [cardsList, userData?.cards, setUserProfile])

  const setDefaultCard = useMutation(
    async (cardId: string) => {
      return await putDefaultCard(cardId)
    },
    {
      onSettled: () => {
        // keep BOTH invalidations since some screens may still rely on allCards
        queryClient.invalidateQueries('allCards')
        queryClient.invalidateQueries('user-profile')
      },
      onError: () => {
        addToast('error', t('error:errorSomethingWentWrong'))
      }
    }
  )

  const removeCard = useMutation(
    async (cardId: string) => {
      return await deleteCard(cardId)
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries('allCards')
        queryClient.invalidateQueries('user-profile')
      },
      onError: () => {
        addToast('error', t('error:errorSomethingWentWrong'))
      }
    }
  )

  const updateCard = useMutation(
    async ({
      data,
      cardId
    }: {
      data: AddCardInfoRequestDto
      cardId: string
    }) => {
      return await editCard({
        card: { ...data, email: userData.email },
        cardId
      })
    },
    {
      onSuccess: () => {
        setPrefillData(initialPrefillData)
      },
      onError: (resp: any) => {
        addToast('error', resp?.response?.data?.error?.message)
      },
      onSettled: () => {
        queryClient.invalidateQueries('allCards')
        queryClient.invalidateQueries('user-profile')
      }
    }
  )

  const editCreditCard = (data: AddCard, cardId: string) => {
    const reqObj = data as unknown as AddCardInfoRequestDto
    updateCard.mutate({ data: reqObj, cardId })
  }

const openEditCreditCard = (card: Card) => {
  modalData.addModal(
    'Edit Card Address',
    <CreditCard
      editFn={(data: AddCard, cardId: string) => {
        editCreditCard(data, cardId)
      }}
      prefillData={{
        id: card.id,
        name: card.card_holder ?? '',
        cardNumber: card.card_number ?? '',
        expiration: `${card.expiration_month}/${card.expiration_year}`,
        cvc: '',
        city: card.city ?? '',
        address: card.street ?? '',
        zip: card.post_code ?? '',
        country:
          typeof card.country === 'object'
            ? card.country?.name ?? ''
            : '',
        state:
          typeof card.region === 'object'
            ? card.region?.name ?? ''
            : ''
      }}
    />,
    false,
    false,
    'credit-card-modal'
  )
}

  /**
   * Auto-default card if only one exists and it's not set as default.
   * Works with either data source.
   */
  useEffect(() => {
    if (!cards?.length) return

    if (
      cards.length === 1 &&
      cards[0].id !== String(userData.default_card ?? '') &&
      !cards[0].is_default
    ) {
      setDefaultCard.mutate(cards[0].id)
    }
  }, [cards, userData?.default_card])

  return (
    <>
      {cards?.map((card: Card) => (
        <ActionCard
          key={card.id}
          icon={
            card.card_number?.charAt?.(0) === '3' ? (
              <spriteIcons.IconVisa />
            ) : (
              <spriteIcons.IconMasterCard />
            )
          }
          text={
            <>
              {card.card_number?.charAt?.(0) === '3' ? 'Visa' : 'Mastercard'}{' '}
              {card.is_verified && <CardText>Verified</CardText>}
            </>
          }
          subtext={<CardNumber>{card.card_number}</CardNumber>}
          description={
            <CardDescription $expired={card.is_expired}>
              {card.is_expired ? 'Expired' : 'Exp'} {card.expiration_month}/{card.expiration_year}
            </CardDescription>
          }
          selected={card.is_default}
          clickFn={() => {
            if (userData?.status === 'restricted' && userData?.role === 'fan') {
              addToast('error', 'Your account is restricted.')
              return
            }
            setDefaultCard.mutate(card.id)
          }}
          trashFn={() => {
            if (userData?.status === 'restricted' && userData?.role === 'fan') {
              addToast('error', 'Your account is restricted.')
              return
            }

            modalData.addModal(
              'Delete Card',
              <ConfirmModal
                body={`Are you sure you want to delete the card ending in ${card.card_number.slice(
                  -4
                )}?`}
                confirmFn={() => {
                  removeCard.mutate(card.id)
                }}
              />
            )
          }}
          editFn={() => {
            if (userData?.status === 'restricted' && userData?.role === 'fan') {
              addToast('error', 'Your account is restricted.')
              return
            }

            openEditCreditCard(card)
          }}
/*           bottomArea={
            <>
              {!card.is_verified && (
                <CardVerifyArea>
                  <CardVerifyButton>Verify</CardVerifyButton>
                  <CardVerifyText>
                    {t('verifyYourCardInOrderToIncreaseSpendingLimits')}
                  </CardVerifyText>
                </CardVerifyArea>
              )}
            </>
          } */
        />
      ))}
    </>
  )
}

const CardDescription = styled.span<{ $expired?: boolean }>`
  color: ${({ $expired }) =>
    $expired ? colors.red : colors.silverGray};
`
const CardText = styled.span`
  font-size: 1.2rem;
  color: ${colors.stormyBlue};
`

const CardNumber = styled.span`
  color: ${colors.stormyBlue};
`

const CardVerifyArea = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`

const CardVerifyButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 1.2rem;
  text-align: center;
  border: 0.1rem solid rgba(103, 110, 125, 0.8);
  background: ${colors.white};
  border-radius: 2.5rem;
  color: ${colors.grey};
  cursor: pointer;
  padding: 0.5rem 2.2rem;
  margin-right: 1.5rem;
`

const CardVerifyText = styled.div`
  font-weight: 500;
  font-size: 1.2rem;
  color: ${colors.steelBlueGray};
`